/**
 * Avisos breves (toast) abajo a la derecha: «Tarea CDT-30 creada.», «CDT-4 movida a «En curso».»,
 * errores y advertencias.
 *
 * Cualquier componente puede mostrar uno con `useAvisos().mostrar(texto, tipo, { accion })`. Se cierran
 * solos y se anuncian a lectores de pantalla (`role="status"` / `role="alert"`). Un aviso con acción
 * (p. ej. «Reintentar») dura más y no se cierra mientras el mouse o el foco están sobre él, para que se
 * alcance a pulsar con teclado.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import './Avisos.css';

type TipoAviso = 'ok' | 'advertencia' | 'error';

export interface AccionAviso {
  texto: string;
  alPulsar: () => void;
}

interface Aviso {
  id: number;
  tipo: TipoAviso;
  texto: string;
  accion?: AccionAviso;
}

interface ContextoAvisos {
  mostrar: (texto: string, tipo?: TipoAviso, opciones?: { accion?: AccionAviso }) => void;
}

const Contexto = createContext<ContextoAvisos | null>(null);
/** Cuánto queda a la vista un aviso; con acción, más (hay que alcanzar a pulsarla). */
export const DURACION_AVISO_MS = 4000;
export const DURACION_AVISO_CON_ACCION_MS = 8000;
let siguienteId = 1;

const duracionDe = (aviso: Aviso) =>
  aviso.accion ? DURACION_AVISO_CON_ACCION_MS : DURACION_AVISO_MS;

export function ProveedorAvisos({ children }: { children: ReactNode }) {
  const [avisos, setAvisos] = useState<Aviso[]>([]);
  const temporizadores = useRef(new Map<number, number>());

  const quitar = useCallback((id: number) => {
    const temporizador = temporizadores.current.get(id);
    if (temporizador !== undefined) window.clearTimeout(temporizador);
    temporizadores.current.delete(id);
    setAvisos((actuales) => actuales.filter((aviso) => aviso.id !== id));
  }, []);

  const programar = useCallback(
    (aviso: Aviso) => {
      const anterior = temporizadores.current.get(aviso.id);
      if (anterior !== undefined) window.clearTimeout(anterior);
      temporizadores.current.set(
        aviso.id,
        window.setTimeout(() => quitar(aviso.id), duracionDe(aviso)),
      );
    },
    [quitar],
  );

  const pausar = useCallback((id: number) => {
    const temporizador = temporizadores.current.get(id);
    if (temporizador !== undefined) window.clearTimeout(temporizador);
    temporizadores.current.delete(id);
  }, []);

  const mostrar = useCallback<ContextoAvisos['mostrar']>(
    (texto, tipo = 'ok', opciones = {}) => {
      const aviso: Aviso = { id: siguienteId++, tipo, texto, accion: opciones.accion };
      setAvisos((actuales) => [...actuales, aviso]);
      programar(aviso);
    },
    [programar],
  );

  // Al desmontar no quedan temporizadores vivos.
  useEffect(() => {
    const mapa = temporizadores.current;
    return () => {
      for (const temporizador of mapa.values()) window.clearTimeout(temporizador);
      mapa.clear();
    };
  }, []);

  const valor = useMemo(() => ({ mostrar }), [mostrar]);

  return (
    <Contexto.Provider value={valor}>
      {children}
      <div className="avisos">
        {avisos.map((aviso) => (
          <div
            key={aviso.id}
            className={`aviso aviso--${aviso.tipo}`}
            role={aviso.tipo === 'error' ? 'alert' : 'status'}
            onMouseEnter={() => pausar(aviso.id)}
            onMouseLeave={() => programar(aviso)}
            onFocus={() => pausar(aviso.id)}
            onBlur={() => programar(aviso)}
          >
            <span className="aviso__texto">{aviso.texto}</span>
            {aviso.accion && (
              <button
                type="button"
                className="aviso__accion"
                onClick={() => {
                  quitar(aviso.id);
                  aviso.accion?.alPulsar();
                }}
              >
                {aviso.accion.texto}
              </button>
            )}
          </div>
        ))}
      </div>
    </Contexto.Provider>
  );
}

export function useAvisos(): ContextoAvisos {
  const contexto = useContext(Contexto);
  if (!contexto) throw new Error('useAvisos debe usarse dentro de <ProveedorAvisos>.');
  return contexto;
}
